import React from 'react';
import { Timestamp } from 'firebase/firestore';

// --- NUEVO COMPONENTE INTERNO PARA CABECERAS ORDENABLES (VERSIÓN MEJORADA) ---
const SortableHeader = ({ children, columnKey, sortCriteria, setSortCriteria }) => {
    const isSorted = sortCriteria.startsWith(columnKey);
    const isAsc = isSorted && sortCriteria.endsWith('Asc');
    const isDesc = isSorted && !isAsc;

    const handleClick = () => {
        if (isSorted) {
            setSortCriteria(isAsc ? `${columnKey}Desc` : `${columnKey}Asc`);
        } else {
            // Por defecto, la primera vez que se hace clic se ordena descendente para fechas y ascendente para texto.
            setSortCriteria(columnKey.includes('Date') ? `${columnKey}Desc` : `${columnKey}Asc`);
        }
    };

    return (
        <th className="group text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer" onClick={handleClick}>
            <div className="flex items-center">
                <span>{children}</span>
                {/* Contenedor para los iconos de ordenamiento */}
                <div className="ml-1.5 flex flex-col">
                    {/* Flecha hacia arriba: visible y oscura si está activa, o visible en hover */}
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mb-1 transition-colors ${isAsc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                    {/* Flecha hacia abajo: visible y oscura si está activa, o visible en hover */}
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 -mt-1 transition-colors ${isDesc ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>
        </th>
    );
};


// --- FUNCIONES AUXILIARES (DE TU CÓDIGO ORIGINAL) ---
const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return 'Fecha Inválida';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (value) => {
    return `Q${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// --- COMPONENTE PRINCIPAL (ACTUALIZADO) ---
function PackageList({ packages, onDelete, showEditButton, onEdit, sortCriteria, setSortCriteria }) {
    if (!packages || packages.length === 0) {
        return (
            <div className="text-center py-10 bg-white rounded-lg shadow">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-4 text-gray-500">No hay paquetes registrados que coincidan con los filtros.</p>
            </div>
        );
    }

    const getStatusColor = (status) => {
        switch (status ? status.toLowerCase() : '') {
            case 'entregado': return 'bg-green-100 text-green-700';
            case 'enviado': return 'bg-blue-100 text-blue-700';
            case 'anulado': return 'bg-red-100 text-red-700';
            case 'devolución': return 'bg-orange-100 text-orange-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };
    
    return (
        <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
            <table className="w-full min-w-[1100px] table-auto">
                <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">No.</th>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider"># Pedido</th>
                        {/* Se usan los nuevos encabezados ordenables */}
                        <SortableHeader columnKey="shipDate" sortCriteria={sortCriteria} setSortCriteria={setSortCriteria}>Fecha Envío</SortableHeader>
                        <SortableHeader columnKey="courierName" sortCriteria={sortCriteria} setSortCriteria={setSortCriteria}>Mensajero</SortableHeader>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Producto</th>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Envío</th>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Total</th>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Gastos</th>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">T. Neto</th>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Estado</th>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Pago</th>
                        <SortableHeader columnKey="deliveryDate" sortCriteria={sortCriteria} setSortCriteria={setSortCriteria}>F. Entrega</SortableHeader>
                        <th className="text-center p-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {packages.map((pkg, index) => {
                        const totalAmount = (parseFloat(pkg.productValue) || 0) + (parseFloat(pkg.shippingCost) || 0);
                        const totalNetAmount = totalAmount - (parseFloat(pkg.expenses) || 0);
                        return (
                            <tr key={pkg.id} className="hover:bg-sky-50 transition duration-150 text-sm">
                                <td className="p-3 text-gray-700 whitespace-nowrap">{index + 1}</td>
                                <td className="p-3 text-sky-700 font-medium whitespace-nowrap">{pkg.orderId}</td>
                                <td className="p-3 text-gray-700 whitespace-nowrap">{formatDate(pkg.shipDate)}</td>
                                <td className="p-3 text-gray-700">{pkg.courierName}</td>
                                <td className="p-3 text-gray-700 whitespace-nowrap text-right">{formatCurrency(pkg.productValue)}</td>
                                <td className="p-3 text-gray-700 whitespace-nowrap text-right">{formatCurrency(pkg.shippingCost)}</td>
                                <td className="p-3 text-gray-800 font-semibold whitespace-nowrap text-right">{formatCurrency(totalAmount)}</td>
                                <td className="p-3 text-red-600 whitespace-nowrap text-right">{formatCurrency(pkg.expenses)}</td>
                                <td className="p-3 text-green-700 font-bold whitespace-nowrap text-right">{formatCurrency(totalNetAmount)}</td>
                                <td className="p-3 whitespace-nowrap"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(pkg.status)}`}>{pkg.status}</span></td>
                                <td className="p-3 text-gray-700 whitespace-nowrap">{pkg.paymentMethod}</td>
                                <td className="p-3 text-gray-700 whitespace-nowrap">{formatDate(pkg.deliveryDate)}</td>
                                <td className="p-3 whitespace-nowrap">
                                    <div className="flex justify-center space-x-1">
                                        {showEditButton && onEdit && (
                                            <button onClick={() => onEdit(pkg)} className="text-sky-600 hover:text-sky-800 p-1 rounded hover:bg-sky-100" title="Editar">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                            </button>
                                        )}
                                        {onDelete && (
                                            <button onClick={() => onDelete(pkg.id)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100" title="Eliminar">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default PackageList;